// Medical Scores Database - Complete Collection Part 14
// Additional scores from medicalcul.free.fr - M-R section

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Scale,
  Clock,
  AlertTriangle,
  Calculator,
  User,
  Beaker,
  Target
} from 'lucide-react';

export const SCORES_PART_14 = {
  // ==================== SCORES M ====================
  mannheim: {
    name: 'Mannheim Peritonitis Index',
    category: 'Chirurgie',
    icon: Syringe,
    description: 'Mortalité sur péritonite',
    color: 'red',
    fields: [
      { id: 'age', label: 'Âge > 50 ans', type: 'checkbox', points: 5 },
      { id: 'female', label: 'Sexe féminin', type: 'checkbox', points: 5 },
      { id: 'organFailure', label: 'Défaillance d\'organe', type: 'checkbox', points: 7 },
      { id: 'malignancy', label: 'Néoplasie', type: 'checkbox', points: 4 },
      { id: 'duration', label: 'Délai > 24h pré-opératoire', type: 'checkbox', points: 4 },
      { id: 'notColon', label: 'Origine non colique', type: 'checkbox', points: 4 },
      { id: 'diffuse', label: 'Péritonite généralisée', type: 'checkbox', points: 6 },
      { id: 'exudate', label: 'Exsudat', type: 'select', options: [
        { value: 0, label: 'Clair' },
        { value: 6, label: 'Trouble/purulent' },
        { value: 12, label: 'Stercorale' }
      ]}
    ],
    calculate: (values) => {
      let total = values.exudate || 0;
      if (values.age) total += 5;
      if (values.female) total += 5;
      if (values.organFailure) total += 7;
      if (values.malignancy) total += 4;
      if (values.duration) total += 4;
      if (values.notColon) total += 4;
      if (values.diffuse) total += 6;
      return { score: total, max: 47 };
    },
    interpret: (score) => {
      if (score < 21) return { level: 'success', text: `MPI ${score}`, detail: 'Mortalité ~0%. Bon pronostic.' };
      if (score < 29) return { level: 'warning', text: `MPI ${score}`, detail: 'Mortalité ~30%. Surveillance réa.' };
      return { level: 'danger', text: `MPI ${score}`, detail: 'Mortalité > 50%. Réanimation intensive.' };
    },
    formula: '< 21: mortalité ~0%. 21-28: ~30%. ≥ 29: > 50%'
  },

  mascc: {
    name: 'MASCC (Neutropénie fébrile)',
    category: 'Oncologie',
    icon: Target,
    description: 'Risque complication sur neutropénie fébrile',
    color: 'red',
    fields: [
      { id: 'burden', label: 'Charge de la maladie', type: 'select', options: [
        { value: 5, label: 'Asymptomatique ou symptômes légers' },
        { value: 3, label: 'Symptômes modérés' },
        { value: 0, label: 'Symptômes sévères ou moribond' }
      ]},
      { id: 'hypotension', label: 'Pas d\'hypotension', type: 'checkbox', points: 5 },
      { id: 'copd', label: 'Pas de BPCO', type: 'checkbox', points: 4 },
      { id: 'solid', label: 'Tumeur solide ou lymphome sans infection fongique antérieure', type: 'checkbox', points: 4 },
      { id: 'dehydration', label: 'Pas de déshydratation', type: 'checkbox', points: 3 },
      { id: 'outpatient', label: 'Patient ambulatoire au début fièvre', type: 'checkbox', points: 3 },
      { id: 'age', label: 'Âge < 60 ans', type: 'checkbox', points: 2 }
    ],
    calculate: (values) => {
      let total = values.burden || 0;
      if (values.hypotension) total += 5;
      if (values.copd) total += 4;
      if (values.solid) total += 4;
      if (values.dehydration) total += 3;
      if (values.outpatient) total += 3;
      if (values.age) total += 2;
      return { score: total, max: 26 };
    },
    interpret: (score) => {
      if (score >= 21) return { level: 'success', text: `MASCC ${score}`, detail: 'Faible risque (< 5%). Traitement ambulatoire possible.' };
      return { level: 'danger', text: `MASCC ${score}`, detail: 'Haut risque. Hospitalisation et ATB IV.' };
    },
    formula: 'Score ≥ 21: faible risque, traitement ambulatoire possible'
  },

  mgap: {
    name: 'MGAP (Traumatisme)',
    category: 'Urgences',
    icon: AlertTriangle,
    description: 'Gravité polytraumatisme en pré-hospitalier',
    color: 'red',
    fields: [
      { id: 'mechanism', label: 'Mécanisme du traumatisme', type: 'select', options: [
        { value: 0, label: 'Pénétrant (arme blanche, balle)' },
        { value: 4, label: 'Non pénétrant (AVP, chute...)' }
      ]},
      { id: 'gcs', label: 'Score de Glasgow', type: 'select', options: [
        { value: 0, label: '3-8' },
        { value: 4, label: '9-12' },
        { value: 6, label: '13-15' }
      ]},
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 5, label: '< 60 ans' },
        { value: 0, label: '≥ 60 ans' }
      ]},
      { id: 'sbp', label: 'PAS (mmHg)', type: 'select', options: [
        { value: 0, label: '< 60' },
        { value: 3, label: '60-120' },
        { value: 6, label: '> 120' }
      ]}
    ],
    calculate: (values) => {
      const total = (values.mechanism || 0) + (values.gcs || 0) + (values.age || 0) + (values.sbp || 0);
      return { score: total, max: 29 };
    },
    interpret: (score) => {
      if (score >= 23) return { level: 'success', text: `MGAP ${score}`, detail: 'Mortalité < 5%. Surveillance standard.' };
      if (score >= 18) return { level: 'warning', text: `MGAP ${score}`, detail: 'Mortalité ~15%. Surveillance trauma center.' };
      return { level: 'danger', text: `MGAP ${score}`, detail: 'Mortalité > 30%. Alerte trauma, damage control.' };
    },
    formula: '< 18: haut risque. 18-22: intermédiaire. ≥ 23: faible risque'
  },

  mrcMuscle: {
    name: 'MRC (Force musculaire)',
    category: 'Neurologie',
    icon: Activity,
    description: 'Cotation déficit musculaire',
    color: 'purple',
    fields: [
      { id: 'force', label: 'Force musculaire', type: 'select', options: [
        { value: 0, label: '0 - Aucune contraction visible' },
        { value: 1, label: '1 - Contraction visible sans mouvement' },
        { value: 2, label: '2 - Mouvement possible sans pesanteur' },
        { value: 3, label: '3 - Mouvement contre pesanteur' },
        { value: 4, label: '4 - Mouvement contre résistance' },
        { value: 5, label: '5 - Force normale' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.force || 5, max: 5 };
    },
    interpret: (score) => {
      if (score === 5) return { level: 'success', text: `MRC ${score}/5`, detail: 'Force normale.' };
      if (score === 4) return { level: 'success', text: `MRC ${score}/5`, detail: 'Déficit mineur. Mouvement possible contre résistance.' };
      if (score === 3) return { level: 'warning', text: `MRC ${score}/5`, detail: 'Déficit modéré. Mouvement contre pesanteur seulement.' };
      if (score === 2) return { level: 'warning', text: `MRC ${score}/5`, detail: 'Déficit sévère. Mouvement possible sans pesanteur.' };
      if (score === 1) return { level: 'danger', text: `MRC ${score}/5`, detail: 'Déficit majeur. Contraction visible seulement.' };
      return { level: 'danger', text: `MRC ${score}/5`, detail: 'Paralysie complète. Aucune contraction.' };
    },
    formula: 'Échelle de 0 (paralysie) à 5 (normal) par groupe musculaire'
  },

  // ==================== SCORES N-P ====================
  news2: {
    name: 'NEWS 2 (National Early Warning Score)',
    category: 'Urgences',
    icon: Activity,
    description: 'Détection précoce détérioration clinique',
    color: 'red',
    fields: [
      { id: 'rr', label: 'Fréquence respiratoire (/min)', type: 'select', options: [
        { value: 3, label: '≤ 8' },
        { value: 1, label: '9-11' },
        { value: 0, label: '12-20' },
        { value: 2, label: '21-24' },
        { value: 3, label: '≥ 25' }
      ]},
      { id: 'spo2', label: 'SpO2 (%) si BPCO non ciblée', type: 'select', options: [
        { value: 3, label: '≤ 91' },
        { value: 2, label: '92-93' },
        { value: 1, label: '94-95' },
        { value: 0, label: '≥ 96' }
      ]},
      { id: 'o2', label: 'Oxygénothérapie', type: 'select', options: [
        { value: 0, label: 'Air ambiant' },
        { value: 2, label: 'Sous O2' }
      ]},
      { id: 'sbp', label: 'PAS (mmHg)', type: 'select', options: [
        { value: 3, label: '≤ 90' },
        { value: 2, label: '91-100' },
        { value: 1, label: '101-110' },
        { value: 0, label: '111-219' },
        { value: 3, label: '≥ 220' }
      ]},
      { id: 'hr', label: 'FC (/min)', type: 'select', options: [
        { value: 3, label: '≤ 40' },
        { value: 1, label: '41-50' },
        { value: 0, label: '51-90' },
        { value: 1, label: '91-110' },
        { value: 2, label: '111-130' },
        { value: 3, label: '≥ 131' }
      ]},
      { id: 'consciousness', label: 'Conscience', type: 'select', options: [
        { value: 0, label: 'Alerte' },
        { value: 3, label: 'Confusion nouvelle ou CVPU' }
      ]},
      { id: 'temp', label: 'Température (°C)', type: 'select', options: [
        { value: 3, label: '≤ 35.0' },
        { value: 1, label: '35.1-36.0' },
        { value: 0, label: '36.1-38.0' },
        { value: 1, label: '38.1-39.0' },
        { value: 2, label: '≥ 39.1' }
      ]}
    ],
    calculate: (values) => {
      const total = (values.rr || 0) + (values.spo2 || 0) + (values.o2 || 0) +
                    (values.sbp || 0) + (values.hr || 0) + (values.consciousness || 0) + (values.temp || 0);
      return { score: total, max: 20 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: `NEWS 2 ${score}`, detail: 'Risque faible. Surveillance standard toutes les 12h.' };
      if (score <= 4) return { level: 'success', text: `NEWS 2 ${score}`, detail: 'Risque faible. Surveillance toutes les 4-6h.' };
      if (score <= 6) return { level: 'warning', text: `NEWS 2 ${score}`, detail: 'Risque modéré. Surveillance horaire, avis senior.' };
      return { level: 'danger', text: `NEWS 2 ${score}`, detail: 'Risque élevé. Surveillance continue, appel équipe urgence.' };
    },
    formula: '0: surveillance 12h. 1-4: 4-6h. 5-6: horaire. ≥ 7: urgente'
  },

  norton: {
    name: 'Norton (Risque escarre)',
    category: 'Dermatologie',
    icon: User,
    description: 'Évaluation risque d\'escarre',
    color: 'orange',
    fields: [
      { id: 'physical', label: 'Condition physique', type: 'select', options: [
        { value: 1, label: '1 - Très mauvaise' },
        { value: 2, label: '2 - Mauvaise' },
        { value: 3, label: '3 - Moyenne' },
        { value: 4, label: '4 - Bonne' }
      ]},
      { id: 'mental', label: 'État mental', type: 'select', options: [
        { value: 1, label: '1 - Stuporeux' },
        { value: 2, label: '2 - Confus' },
        { value: 3, label: '3 - Apathique' },
        { value: 4, label: '4 - Alerte' }
      ]},
      { id: 'activity', label: 'Activité', type: 'select', options: [
        { value: 1, label: '1 - Alité' },
        { value: 2, label: '2 - Fauteuil' },
        { value: 3, label: '3 - Marche avec aide' },
        { value: 4, label: '4 - Ambulant' }
      ]},
      { id: 'mobility', label: 'Mobilité', type: 'select', options: [
        { value: 1, label: '1 - Immobile' },
        { value: 2, label: '2 - Très limité' },
        { value: 3, label: '3 - Légèrement limité' },
        { value: 4, label: '4 - Complète' }
      ]},
      { id: 'incontinence', label: 'Incontinence', type: 'select', options: [
        { value: 1, label: '1 - Double incontinence' },
        { value: 2, label: '2 - Urinaire habituelle' },
        { value: 3, label: '3 - Occasionnelle' },
        { value: 4, label: '4 - Aucune' }
      ]}
    ],
    calculate: (values) => {
      const total = (values.physical || 4) + (values.mental || 4) + (values.activity || 4) +
                    (values.mobility || 4) + (values.incontinence || 4);
      return { score: total, max: 20 };
    },
    interpret: (score) => {
      if (score <= 10) return { level: 'danger', text: `Norton ${score}/20`, detail: 'Risque très élevé d\'escarre. Prévention intensive.' };
      if (score <= 12) return { level: 'danger', text: `Norton ${score}/20`, detail: 'Risque élevé. Mobilisation, matelas adapté.' };
      if (score <= 14) return { level: 'warning', text: `Norton ${score}/20`, detail: 'Risque modéré. Prévention standard.' };
      return { level: 'success', text: `Norton ${score}/20`, detail: 'Risque faible. Surveillance habituelle.' };
    },
    formula: '≤ 12: risque élevé. 13-14: modéré. ≥ 15: faible'
  },

  pesi: {
    name: 'PESI (Embolie pulmonaire)',
    category: 'Pneumologie',
    icon: Wind,
    description: 'Gravité et pronostic embolie pulmonaire',
    color: 'blue',
    fields: [
      { id: 'age', label: 'Âge (années)', type: 'number', min: 18, max: 120 },
      { id: 'male', label: 'Sexe masculin', type: 'checkbox', points: 10 },
      { id: 'cancer', label: 'Cancer', type: 'checkbox', points: 30 },
      { id: 'chf', label: 'Insuffisance cardiaque', type: 'checkbox', points: 10 },
      { id: 'copd', label: 'BPCO', type: 'checkbox', points: 10 },
      { id: 'hr', label: 'FC ≥ 110/min', type: 'checkbox', points: 20 },
      { id: 'sbp', label: 'PAS < 100 mmHg', type: 'checkbox', points: 30 },
      { id: 'rr', label: 'FR ≥ 30/min', type: 'checkbox', points: 20 },
      { id: 'temp', label: 'Température < 36°C', type: 'checkbox', points: 20 },
      { id: 'confusion', label: 'Confusion', type: 'checkbox', points: 60 },
      { id: 'spo2', label: 'SpO2 < 90%', type: 'checkbox', points: 20 }
    ],
    calculate: (values) => {
      let total = values.age || 0;
      if (values.male) total += 10;
      if (values.cancer) total += 30;
      if (values.chf) total += 10;
      if (values.copd) total += 10;
      if (values.hr) total += 20;
      if (values.sbp) total += 30;
      if (values.rr) total += 20;
      if (values.temp) total += 20;
      if (values.confusion) total += 60;
      if (values.spo2) total += 20;
      return { score: total, max: 'variable' };
    },
    interpret: (score) => {
      if (score <= 65) return { level: 'success', text: `PESI Classe I`, detail: 'Risque très faible (mortalité ~1%). Traitement ambulatoire possible.' };
      if (score <= 85) return { level: 'success', text: `PESI Classe II`, detail: 'Risque faible (mortalité ~2%). Hospitalisation courte.' };
      if (score <= 105) return { level: 'warning', text: `PESI Classe III`, detail: 'Risque intermédiaire (mortalité ~5%). Hospitalisation.' };
      if (score <= 125) return { level: 'danger', text: `PESI Classe IV`, detail: 'Risque élevé (mortalité ~11%). Surveillance rapprochée.' };
      return { level: 'danger', text: `PESI Classe V`, detail: 'Risque très élevé (mortalité ~25%). USI recommandée.' };
    },
    formula: 'Classe I: ≤65. II: 66-85. III: 86-105. IV: 106-125. V: >125'
  },

  // ==================== SCORES Q-R ====================
  ramsay: {
    name: 'Ramsay (Sédation)',
    category: 'Anesthésie',
    icon: Syringe,
    description: 'Niveau de sédation',
    color: 'blue',
    fields: [
      { id: 'level', label: 'Niveau de sédation', type: 'select', options: [
        { value: 1, label: '1 - Patient anxieux, agité' },
        { value: 2, label: '2 - Patient coopérant, orienté, calme' },
        { value: 3, label: '3 - Répond aux ordres' },
        { value: 4, label: '4 - Endormi, réponse vive à stimulation glabelle/bruit' },
        { value: 5, label: '5 - Endormi, réponse lente à stimulation' },
        { value: 6, label: '6 - Pas de réponse' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.level || 2, max: 6 };
    },
    interpret: (score) => {
      if (score === 1) return { level: 'danger', text: `Ramsay ${score}`, detail: 'Sédation insuffisante. Patient agité. Augmenter sédation.' };
      if (score <= 3) return { level: 'success', text: `Ramsay ${score}`, detail: 'Sédation légère, patient coopérant. Objectif pour procédures.' };
      if (score <= 5) return { level: 'success', text: `Ramsay ${score}`, detail: 'Sédation profonde. Objectif pour ventilation mécanique.' };
      return { level: 'warning', text: `Ramsay ${score}`, detail: 'Sédation très profonde/coma. Vérifier surdosage.' };
    },
    formula: 'Objectif usuel en réanimation: Ramsay 3-5'
  },

  rankin: {
    name: 'Rankin modifiée (Handicap post-AVC)',
    category: 'Neurologie',
    icon: Brain,
    description: 'Évaluation globale du handicap',
    color: 'purple',
    fields: [
      { id: 'grade', label: 'Grade mRS', type: 'select', options: [
        { value: 0, label: '0 - Aucun symptôme' },
        { value: 1, label: '1 - Symptômes mineurs sans handicap' },
        { value: 2, label: '2 - Handicap léger, autonome' },
        { value: 3, label: '3 - Handicap modéré, marche sans aide, assistance partielle' },
        { value: 4, label: '4 - Handicap modérément sévère, aide quotidienne' },
        { value: 5, label: '5 - Handicap sévère, alité, soins constants' },
        { value: 6, label: '6 - Décès' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.grade || 0, max: 6 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `mRS ${score}`, detail: 'Excellent pronostic. Autonomie complète.' };
      if (score === 2) return { level: 'success', text: `mRS ${score}`, detail: 'Bon pronostic. Reprise activités avec limitations.' };
      if (score === 3) return { level: 'warning', text: `mRS ${score}`, detail: 'Pronostic réservé. Autonomie partielle, aide nécessaire.' };
      if (score === 4) return { level: 'danger', text: `mRS ${score}`, detail: 'Handicap important. Dépendance quotidienne.' };
      if (score === 5) return { level: 'danger', text: `mRS ${score}`, detail: 'Handicap majeur. Soins permanents.' };
      return { level: 'danger', text: `mRS ${score}`, detail: 'Décès.' };
    },
    formula: 'Échelle de référence pour les essais cliniques AVC. mRS ≤ 2 = bon outcome'
  },

  rass: {
    name: 'RASS (Richmond Agitation-Sedation)',
    category: 'Anesthésie',
    icon: Syringe,
    description: 'Niveau de sédation/agitation',
    color: 'blue',
    fields: [
      { id: 'level', label: 'Niveau RASS', type: 'select', options: [
        { value: 4, label: '+4 - Combatif, violent, danger immédiat' },
        { value: 3, label: '+3 - Très agité, arrache tubes/cathéters' },
        { value: 2, label: '+2 - Agité, mouvements fréquents non coordonnés' },
        { value: 1, label: '+1 - Anxieux mais mouvements non agressifs' },
        { value: 0, label: '0 - Éveillé et calme' },
        { value: -1, label: '-1 - Somnolent, éveil > 10s à la voix' },
        { value: -2, label: '-2 - Sédation légère, éveil < 10s à la voix' },
        { value: -3, label: '-3 - Sédation modérée, mouvement à la voix' },
        { value: -4, label: '-4 - Sédation profonde, mouvement à stimulation physique' },
        { value: -5, label: '-5 - Non éveillable, pas de réponse' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.level || 0, max: '+4 à -5' };
    },
    interpret: (score) => {
      if (score >= 3) return { level: 'danger', text: `RASS ${score > 0 ? '+' : ''}${score}`, detail: 'Agitation sévère. Risque auto-extubation. Augmenter sédation.' };
      if (score >= 1) return { level: 'warning', text: `RASS +${score}`, detail: 'Agitation. Évaluer cause (douleur, delirium). Adapter sédation.' };
      if (score === 0) return { level: 'success', text: `RASS 0`, detail: 'Patient calme et éveillé. État optimal.' };
      if (score >= -2) return { level: 'success', text: `RASS ${score}`, detail: 'Sédation légère. Objectif pour sevrage ventilatoire.' };
      if (score >= -3) return { level: 'success', text: `RASS ${score}`, detail: 'Sédation modérée. Objectif pour ventilation mécanique.' };
      return { level: 'warning', text: `RASS ${score}`, detail: 'Sédation profonde. Vérifier surdosage, évaluer CAM-ICU.' };
    },
    formula: 'Objectif usuel en réa: RASS -2 à 0. Sevrage ventilation: RASS 0 à -1'
  },

  roxIndex: {
    name: 'ROX Index (Oxygénothérapie haut débit)',
    category: 'Réanimation',
    icon: Activity,
    description: 'Prédiction échec oxygénothérapie haut débit nasal',
    color: 'blue',
    fields: [
      { id: 'spo2', label: 'SpO2 (%)', type: 'number', min: 70, max: 100 },
      { id: 'fio2', label: 'FiO2 (%)', type: 'number', min: 21, max: 100 },
      { id: 'rr', label: 'Fréquence respiratoire (/min)', type: 'number', min: 10, max: 60 }
    ],
    calculate: (values) => {
      const spo2 = values.spo2 || 96;
      const fio2 = values.fio2 || 50;
      const rr = values.rr || 25;
      const rox = (spo2 / fio2) / rr * 100;
      return { score: Math.round(rox * 100) / 100, max: 'variable' };
    },
    interpret: (score) => {
      if (score >= 4.88) return { level: 'success', text: `ROX ${score}`, detail: 'Faible risque d\'intubation. Poursuite OHD.' };
      if (score >= 3.85) return { level: 'warning', text: `ROX ${score}`, detail: 'Zone grise. Surveillance rapprochée, réévaluation à 12h.' };
      return { level: 'danger', text: `ROX ${score}`, detail: 'Haut risque d\'intubation. Envisager IOT précoce.' };
    },
    formula: 'ROX = (SpO2/FiO2)/FR × 100. < 3.85 à H12: intubation probable'
  }
};
